import {
  ClosestProviderReportModel,
  ExportAccessReportsUsageModel,
  ExportAccuracyActionableProvidersBulkModel,
  ExportAccuracyActionableProvidersModel,
  ExportAccuracyAggregatesModel,
  ExportAccuracySummariesModel,
  ExportAdequacyAggregatesModel,
  ExportAdequacyServicingProvidersModel,
  ExportAdequacySummariesModel,
  ExportAttestationAggregatesModel,
  ExportAttestationServicingProvidersModel,
  ExportAttestationSummariesModel,
  ExportBenchmarkingDashboardDetailsModel,
  ExportBenchmarkingDashboardDetailsReportModel,
  ExportBulkSpecialtyGroupExceptionsModel,
  ExportBulkSpecialtySummaryByZipRequestModel,
  ExportBulkZipClassAggregateResultsRequestModel,
  ExportCCIIODataValidationsModel,
  ExportCCIIOProviderModel,
  ExportCompositeAggregatesModel,
  ExportCompositeServicingProvidersModel,
  ExportCompositeSummariesModel,
  ExportNetworkIntegrityProviderReportModel,
  ExportNetworkIntegrityGapImpactReportModel,
  ExportNewProviderReportModel,
  ExportPciCrossProjectRequestModel,
  ExportProvidersModel,
  ExportRecommendedActionReasonModel,
  ExportStatusReportModel,
  ExportSummariesDetailProvidersRequestModel,
  ExportUserLoginStatisticsRequestModel,
  ExportAdequacySpecialtySummaryProjectModel,
  ExportAdequacyVolumeAggregateRequestModel,
  ExportZipAnalysisRequestModel,
  ReportSettingsModel,
  SharedUserModel,
  VolumeAdequacySpecialtySummariesRequestModel,
  VolumeServicingProvidersRequestModel,
  VolumeSummariesRequestModel,
  VolumeZipSummariesRequestModel,
  ZipSummariesReportModel,
  ExportProviderHubReportModel
} from "./exportModels";
import { BulkProjectTypeEnum } from "@app/targeting/import/interfaces";
import { ColumnConfigurationPresetOption } from "@app/report-customization/build-reports/interfaces/columnConfigurationPresetOption";
import { ExportColumn } from "@app/report-customization/build-reports/interfaces/exportColumns";
import { ColumnConfigurationPreset } from "@app/report-customization/build-reports/interfaces/columnConfigurationPreset";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { ReportSearchColumn } from "@app/report-customization/build-reports/interfaces/reportSearchColumnEnum";
import { ReportPresetsApiResponse } from "@app/report-customization/manage-presets/interfaces/reportPresetsApiResponse";
import { ReportPreset } from "@app/report-customization/build-reports/interfaces/reportPreset";
import ServiceBase from "@app/shared/serviceBase";
import { querystringify } from "@app/shared/utility/querystringify";
import { PciDashboardFiltersPayload } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersPayload";
import { MultiMapExportRequestModel } from "@app/projects/network-assessment/multi-map-export/utils/buildMultiMapExportRequest";
import { NetworkProviderFilterType, AdequacyAggregateType } from "@app/exports/enumerations";
import { NetworkAssessmentAnalysisType } from "@app/projects/enumerators";
import { ReportsUIModel, AvailableFilterCriteria } from "@app/reports/reportsModel";
import { ResourceStatus } from "@app/resources";

/** Manages interaction with the export API. */
export default class ExportService extends ServiceBase {

  // Access Report
  public async postExportAccessUsers(model: ExportAccessReportsUsageModel): Promise<Blob> {
    return await this.makePost<Blob>(`access-usage-report`, model, `${this.apiEndpoint}/exports`, false, "application/json", true, { responseType: "blob" });
  }

  // Accuracy
  public async getExportAccuracyRecommendedActionReasons(projectId: number, projectRunConfigurationId: number, inspectorUids: string[]): Promise<ExportRecommendedActionReasonModel[]> {
    if (inspectorUids && inspectorUids.length > 0) {
      return await this.makeGet<ExportRecommendedActionReasonModel[]>(`exports/accuracy-recommended-action-reasons-inspector-uids`, { inspectorUids: inspectorUids.join(";") });
    }

    return await this.makeGet<ExportRecommendedActionReasonModel[]>(`exports/accuracy-recommended-action-reasons`, { projectId, projectRunConfigurationId });
  }
  public async postExportAccuracyAggregates(model: ExportAccuracyAggregatesModel): Promise<number> {
    return await this.makePost<number>(`exports/accuracy-aggregates`, model);
  }

  public async postExportAccuracySummaries(model: ExportAccuracySummariesModel): Promise<number> {
    return await this.makePost<number>(`exports/accuracy-summaries`, model);
  }

  public async postExportAccuracyActionableProviders(model: ExportAccuracyActionableProvidersModel): Promise<number> {
    return await this.makePost<number>(`exports/accuracy-actionable-providers`, model);
  }

  public async postExportAccuracyActionableProvidersBulk(model: ExportAccuracyActionableProvidersBulkModel): Promise<number> {
    return await this.makePost<number>(`exports/accuracy-actionable-providers-bulk`, model);
  }

  public async postExportAccuracyStatusReport(model: ExportStatusReportModel): Promise<number> {
    return await this.makePost<number>(`exports/accuracy-status-report`, model);
  }

  // Adequacy
  public async postExportAdequacyAggregates(model: ExportAdequacyAggregatesModel): Promise<number> {
    return await this.makePost<number>(`exports/adequacy-aggregates`, model);
  }

  public async postExportAdequacySummaries(model: ExportAdequacySummariesModel): Promise<number> {
    return await this.makePost<number>(`exports/adequacy-summaries`, model);
  }

  public async postExportAdequacyServicingProviders(model: ExportAdequacyServicingProvidersModel): Promise<number> {
    return await this.makePost<number>(`exports/adequacy-servicing-providers`, model);
  }

  public async postExportZipSummaries(model: ZipSummariesReportModel): Promise<number> {
    return await this.makePost<number>(`exports/zip-summaries`, model);
  }

  public async postExportClosestProvider(model: ClosestProviderReportModel): Promise<number> {
    return await this.makePost<number>(`exports/closest-provider`, model);
  }

  public async postExportAdequacySpecialtySummaries(model: ExportAdequacySpecialtySummaryProjectModel): Promise<number> {
    return await this.makePost<number>(`exports/adequacy-specialty-summaries`, model);
  }

  // Attestation
  public async postExportAttestationAggregates(model: ExportAttestationAggregatesModel): Promise<number> {
    return await this.makePost<number>(`exports/attestation-aggregates`, model);
  }

  public async postExportAttestationSummaries(model: ExportAttestationSummariesModel): Promise<number> {
    return await this.makePost<number>(`exports/attestation-summaries`, model);
  }

  public async postExportAttestationServicingProviders(model: ExportAttestationServicingProvidersModel): Promise<number> {
    return await this.makePost<number>(`exports/attestation-servicing-providers`, model);
  }

  // Composite
  public async postExportCompositeAggregates(model: ExportCompositeAggregatesModel): Promise<number> {
    return await this.makePost<number>(`exports/composite-aggregates`, model);
  }

  public async postExportCompositeSummaries(model: ExportCompositeSummariesModel): Promise<number> {
    return await this.makePost<number>(`exports/composite-summaries`, model);
  }

  public async postExportCompositeServicingProviders(model: ExportCompositeServicingProvidersModel): Promise<number> {
    return await this.makePost<number>(`exports/composite-servicing-providers`, model);
  }

  // Multi Map Export
  // Returns the job id, not the report id.
  public async postExportMultiMap(model: MultiMapExportRequestModel, signal?: AbortSignal): Promise<number> {
    return await this.makePost<number>(`exports/multi-map-export`, model, undefined, false, "application/json", true, undefined, signal);
  }

  // Network Integrity
  public async postExportNetworkIntegrityProviders(model: ExportNetworkIntegrityProviderReportModel): Promise<number> {
    return await this.makePost<number>(`exports/network-integrity-providers`, model);
  }

  public async postExportNetworkIntegrityGapImpact(model: ExportNetworkIntegrityGapImpactReportModel): Promise<number> {
    return await this.makePost<number>(`exports/network-integrity-gap-impact`, model);
  }

  // New Provider Report
  public async postNewProviderReport(model: ExportNewProviderReportModel): Promise<number> {
    return await this.makePost<number>(`exports/new-provider-report`, model);
  }

  // Summaries
  public async postSummariesDetailProviders(model: ExportSummariesDetailProvidersRequestModel): Promise<number> {
    return await this.makePost<number>(`exports/summaries-detail-providers`, model);
  }

  // User Login Statistics
  public async postUserLoginStatistics(model: ExportUserLoginStatisticsRequestModel): Promise<number> {
    return await this.makePost<number>(`exports/user-login-statistics`, model);
  }

  // Report Settings
  public async getAllUserReportSettings(): Promise<ReportSettingsModel[]> {
    return await this.makeGet<ReportSettingsModel[]>(`exports/report-settings/all`);
  }

  public async saveReportSettings(model: ReportSettingsModel): Promise<ReportSettingsModel> {
    return await this.makePost<ReportSettingsModel>(`exports/report-settings`, model);
  }

  public async updateReportSettings(models: ReportSettingsModel[]): Promise<boolean> {
    return await this.makePut<boolean>(`exports/report-settings`, models);
  }

  public async deleteReportSettings(ids: number[]): Promise<boolean> {
    return await this.makePost<boolean>(`exports/report-settings/delete`, ids);
  }

  // Reports Page
  public async getAvailableReports(startDate: string, endDate: string, searchColumn: ReportSearchColumn | null, searchValue: string, searchValue2?: Date): Promise<ReportsUIModel[]> {
    const params = new URLSearchParams({
      endDate,
      startDate
    });

    if (searchColumn !== null && searchColumn !== undefined) {
      params.append('searchColumn', searchColumn);
    }

    if (searchValue) {
      params.append('searchValue', searchValue);
    }

    if (searchValue2) {
      params.append('searchValue2', searchValue2.toISOString());
    }

    return await this.makeGet<ReportsUIModel[]>(`exports/getreportsbyuserid?${params.toString()}`);
  }

  public async getAvailableSharedReports(startDate: string, endDate: string, searchColumn: ReportSearchColumn | null, searchValue: string, searchValue2?: Date): Promise<ReportsUIModel[]> {
    const params = new URLSearchParams({
      endDate,
      startDate
    });

    if (searchColumn !== null && searchColumn !== undefined) {
      params.append('searchColumn', searchColumn);
    }

    if (searchValue) {
      params.append('searchValue', searchValue);
    }

    if (searchValue2) {
      params.append('searchValue2', searchValue2.toISOString());
    }

    return await this.makeGet<ReportsUIModel[]>(`exports/getsharedreportsbyuserid?${params.toString()}`);
  }

  public async getAvailableFilterCriteria(startDate: string, endDate: string): Promise<AvailableFilterCriteria> {
    return await this.makeGet<AvailableFilterCriteria>(`exports/getavailablefiltercriteria?startDate=${startDate}&endDate=${endDate}`);
  }

  public getProjectReportLink(projectId: number, reportId: number): string {
    return `${window.apiEndpointV2_0}/exports/download/${projectId}/${reportId}`;
  }

  public getReportLink(reportId: number): string {
    return `${window.apiEndpointV2_0}/exports/download/${reportId}`;
  }

  // Export Job Control
  public async cancelReportJob(reportId: number): Promise<boolean> {
    return await this.makePost<boolean>(`exports/cancelreportjob`, reportId);
  }

  // Bulk/Volume Reports
  public async postVolumeAdequacyAggregates(requestModel: ExportAdequacyVolumeAggregateRequestModel): Promise<number> {
    return await this.makePost<number>(`exports/adequecy-bulk-aggregate`, requestModel);
  }

  public postVolumeSummaries(requestModel: VolumeSummariesRequestModel): Promise<void> {
    return this.makePost("exports/bulk-summaries", requestModel);
  }

  public postVolumeServicingProviders(requestModel: VolumeServicingProvidersRequestModel): Promise<void> {
    return this.makePost("exports/bulk-servicing-providers", requestModel);
  }

  public postVolumeZipSummaries(requestModel: VolumeZipSummariesRequestModel): Promise<void> {
    return this.makePost("exports/bulk-zip-summaries", requestModel);
  }

  public postVolumeZipAnalysisResults(requestModel: ExportBulkSpecialtySummaryByZipRequestModel): Promise<void> {
    return this.makePost("exports/bulk-specialty-summary-by-zip", requestModel);
  }

  public postVolumeZipClassAggregateResults(requestModel: ExportBulkZipClassAggregateResultsRequestModel): Promise<void> {
    return this.makePost("exports/bulk-zip-class-aggregate-results", requestModel);
  }

  public async postExportCCIIOProviders(requestModel: ExportCCIIOProviderModel): Promise<number> {
    return await this.makePost<number>(`exports/cciio-providers`, requestModel);
  }

  public async postExportCCIIOProviderValidations(requestModel: ExportCCIIODataValidationsModel): Promise<number> {
    return await this.makePost<number>(`exports/cciio-validation-data-integrity`, requestModel);
  }

  public async postExportCCIIOProviderValidationsSummary(requestModel: ExportCCIIODataValidationsModel): Promise<number> {
    return await this.makePost<number>(`exports/cciio-provider-validation-summaries`, requestModel);
  }

  public postVolumeSpecialtySummaries(requestModel: VolumeAdequacySpecialtySummariesRequestModel): Promise<void> {
    return this.makePost("exports/bulk-specialty-summaries", requestModel);
  }

  public async getVolumeActionRowCount(rowCount: number): Promise<number> {
    return await this.makeGet(`exports/volume-target-row-count?rowCount=${rowCount}`);
  }

  public async getVolumeActionUploadLimit(): Promise<number> {
    return await this.makeGet('exports/volume-target-upload-limit');
  }

  public async getNetworkServicingProviderUploadLimit(): Promise<number> {
    return await this.makeGet('exports/network-provider-report-limit');
  }

  public async getProspectMarketServicingProviderUploadLimit(): Promise<number> {
    return await this.makeGet('exports/prospect-market-provider-report-limit');
  }

  public async getSharedUsers(exportType: string, customProjectIds: number[], networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
    networkProviderFilterType: NetworkProviderFilterType, aggregateType: AdequacyAggregateType, requiredRoles?: string[]): Promise<SharedUserModel[]> {
    const query = {
      aggregateType,
      networkAssessmentAnalysisType,
      networkProviderFilterType,
      requiredRoles: requiredRoles?.join(',')
    };

    return await this.makePost<SharedUserModel[]>(`exports/sharedUsers/exportType/${exportType}?${querystringify(query)}`, customProjectIds);
  }

  public async postExportPciDashboardDetails(projectId: number, runConfigurationId: number, requestModel: PciDashboardFiltersPayload): Promise<number> {
    return await this.makePost<number>(`exports/project/${projectId}/runconfiguration/${runConfigurationId}/pci-dashboard-details`, requestModel);
  }

  public async postExportPciCrossProject(requestModel: ExportPciCrossProjectRequestModel): Promise<number> {
    return await this.makePost<number>(`exports/pci-cross-project`, requestModel);
  }

  public async postExportBenchmarkingDashboardDetails(requestModel: ExportBenchmarkingDashboardDetailsModel): Promise<number> {
    return await this.makePost<number>(`exports/benchmarking-dashboard-details`, requestModel);
  }

  public async postExportBenchmarkingDashboardReportDetails(requestModel: ExportBenchmarkingDashboardDetailsReportModel): Promise<number> {
    return await this.makePost<number>(`exports/benchmarking-dashboard-details`, requestModel);
  }

  public async exportZipAnalysisResults(payload: ExportZipAnalysisRequestModel): Promise<void> {
    return await this.makePost(`exports/zip-analysis-results`, payload);
  }

  public async exportZipClassAggregateResults(payload: ExportZipAnalysisRequestModel): Promise<void> {
    return await this.makePost(`exports/zip-class-aggregate-results`, payload);
  }

  public async exportProviders(requestModel: ExportProvidersModel): Promise<void> {
    return await this.makePost(`exports/provider-export`, requestModel);
  }

  public async generateBulkSpecialtyGroupExceptionReport(requestModel: ExportBulkSpecialtyGroupExceptionsModel): Promise<number> {
    return await this.makePost<number>(`exports/bulk-exceptions`, requestModel);
  }

  /* Report Customization Endpoints */
  /* This endpoint is for the dropdown on the report customization page */
  public async getColumnCustomizationPresets(reportType: ReportType): Promise<ColumnConfigurationPresetOption[]> {
    return await this.makeGet(`exports/${reportType}/presets`);
  }

  /* And this endpoint is for the list of presets on the manage presets page */
  public async getReportPresets(): Promise<ReportPresetsApiResponse[]> {
    return this.makeGet("exports/presets");
  }

  public async getColumnConfigurationPresetDetails(reportType: ReportType, presetId: number): Promise<ReportPreset> {
    return this.makeGet(`exports/${reportType}/presets/${presetId}`);
  }

  /* Get export columns endpoints */
  public getAdequacyServicingProvidersColumns = async (model: ExportAdequacyServicingProvidersModel): Promise<ExportColumn[]> => {
    return await this.makePost<ExportColumn[]>(`exports/adequacy-servicing-providers/columns`, model);
  }

  public getAdequacyAggregatesColumns = async (model: ExportAdequacyAggregatesModel): Promise<ExportColumn[]> => {
    return await this.makePost<ExportColumn[]>(`exports/adequacy-aggregates/columns`, model);
  }

  public getAdequacyBulkTargetingSpecialtySummaryComparisonsColumns = async (): Promise<ExportColumn[]> => {
    return await this.makeGet<ExportColumn[]>("exports/target-adequacy-specialty-summary-comparison/columns")
  }

  public getAdequacyBulkTargetingChangedGapsColumns = async (): Promise<ExportColumn[]> => {
    return await this.makeGet<ExportColumn[]>("exports/target-adequacy-changed-gaps/columns")
  }

  public getAdequacyBulkTargetingProvidersColumns = async (projectType?: BulkProjectTypeEnum): Promise<ExportColumn[]> => {
    return await this.makeGet<ExportColumn[]>("exports/target-adequacy-providers/columns", { projectType })
  }

  // Zip volume-target projects reuse the Service Area Changed Gaps report type but the generated report uses
  // the zip "Specialty Summary by Zip Code" columns, so the column picker must fetch the zip columns.
  public getAdequacyBulkTargetingZipServiceAreaChangedGapsColumns = async (): Promise<ExportColumn[]> => {
    return await this.makeGet<ExportColumn[]>("exports/target-zip-service-area-changed-gaps/columns")
  }

  // Zip volume-target projects reuse the Service Area Specialty Summary Comparison report type but the
  // generated report uses the zip "Specialty Summary by Zip Code" columns, so the column picker must fetch
  // the zip columns.
  public getAdequacyBulkTargetingZipServiceAreaSpecialtySummaryComparisonColumns = async (): Promise<ExportColumn[]> => {
    return await this.makeGet<ExportColumn[]>("exports/target-zip-service-area-specialty-summary-comparison/columns")
  }

  public getAdequacyBulkTargetingDistinctProvidersColumns = async (projectType?: BulkProjectTypeEnum): Promise<ExportColumn[]> => {
    return await this.makeGet<ExportColumn[]>("exports/target-adequacy-distinct-providers/columns", { projectType })
  }

  public getAdequacyBulkTargetingProjectDetailsColumns = async (projectType?: BulkProjectTypeEnum): Promise<ExportColumn[]> => {
    return await this.makeGet<ExportColumn[]>("exports/target-project-details/columns", { projectType })
  }

  public getAdequacySpecialtySummaryColumns = async (model: ExportAdequacySpecialtySummaryProjectModel): Promise<ExportColumn[]> => {
    return await this.makePost<ExportColumn[]>(`exports/adequacy-specialty-summaries/columns`, model);
  }

  public getClosestProviderColumns = async (model: ClosestProviderReportModel): Promise<ExportColumn[]> => {
    return await this.makePost<ExportColumn[]>(`exports/closest-provider/columns`, model);
  }

  public getZipSummariesColumns = async (model: ZipSummariesReportModel): Promise<ExportColumn[]> => {
    return await this.makePost<ExportColumn[]>(`exports/zip-summaries/columns`, model);
  }

  // summary by zip classification
  public getZipClassAggregatesColumns = async (model: ExportZipAnalysisRequestModel): Promise<ExportColumn[]> => {
    return await this.makePost<ExportColumn[]>("exports/zip-class-aggregates/columns", model);
  }

  // specialty summary by zip code
  public getZipAnalysisResultsColumns = async (model: ExportZipAnalysisRequestModel): Promise<ExportColumn[]> => {
    return await this.makePost<ExportColumn[]>("exports/zip-analysis-results/columns", model);
  }

  public getProviderHubColumns = async (model: ExportProviderHubReportModel): Promise<ExportColumn[]> => {
    return await this.makePost<ExportColumn[]>(`exports/provider-hub/columns`, model);
  }

  /* subsection: Volume Terminations Columns Endpoints */
  // These are all POSTs for consistency but don't have a request body
  public getAdequacyBulkTerminationSpecialtySummaryComparisonsColumns = async (): Promise<ExportColumn[]> => {
    return await this.makePost<ExportColumn[]>("exports/termination-adequacy-specialty-summary-comparison/columns");
  }

  public getAdequacyBulkTerminationChangedGapsColumns = async (): Promise<ExportColumn[]> => {
    return await this.makePost<ExportColumn[]>("exports/termination-adequacy-changed-gaps/columns");
  }

  // Zip volume-termination projects reuse the Service Area Changed Gaps report type but the generated report
  // uses the zip "Specialty Summary by Zip Code" columns, so the column picker must fetch the zip columns.
  public getAdequacyBulkTerminationZipServiceAreaChangedGapsColumns = async (): Promise<ExportColumn[]> => {
    return await this.makePost<ExportColumn[]>("exports/termination-zip-service-area-changed-gaps/columns");
  }

  // Zip volume-termination projects reuse the Service Area Specialty Summary Comparison report type but the
  // generated report uses the zip "Specialty Summary by Zip Code" columns, so the column picker must fetch
  // the zip columns.
  public getAdequacyBulkTerminationZipServiceAreaSpecialtySummaryComparisonColumns = async (): Promise<ExportColumn[]> => {
    return await this.makePost<ExportColumn[]>("exports/termination-zip-service-area-specialty-summary-comparison/columns");
  }

  public getAdequacyBulkTerminationProvidersColumns = async (projectType?: BulkProjectTypeEnum): Promise<ExportColumn[]> => {
    const query = projectType !== undefined ? `?projectType=${projectType}` : "";
    return await this.makePost<ExportColumn[]>(`exports/termination-adequacy-providers/columns${query}`);
  }

  public getAdequacyBulkTerminationDistinctProvidersColumns = async (projectType?: BulkProjectTypeEnum): Promise<ExportColumn[]> => {
    const query = projectType !== undefined ? `?projectType=${projectType}` : "";
    return await this.makePost<ExportColumn[]>(`exports/termination-adequacy-distinct-providers/columns${query}`);
  }

  public getAdequacyVolumeTermProjectDetailsColumns = async (projectType?: BulkProjectTypeEnum): Promise<ExportColumn[]> => {
    const query = projectType !== undefined ? `?projectType=${projectType}` : "";
    return await this.makePost<ExportColumn[]>(`exports/termination-adequacy-volume-term-project-details/columns${query}`);
  }

  /* End get columns endpoints */

  public async saveNewColumnConfigurationPreset(preset: ColumnConfigurationPreset): Promise<ReportPreset> {
    return await this.makePost("exports/save-preset", preset);
  }

  public async editColumnConfigurationPreset(preset: ColumnConfigurationPreset, reportCustomizationId: number): Promise<void> {
    return await this.makePost(`exports/presets/${reportCustomizationId}`, preset);
  }

  public async deletePreset(reportCustomizationId: number, newStatus: ResourceStatus): Promise<void> {
    return await this.makeDelete(`exports/presets/${reportCustomizationId}`, newStatus);
  }

  public async postExportProviderHubReport(model: ExportProviderHubReportModel): Promise<number> {
    return await this.makePost<number>(`exports/access-points-export`, model);
  }
}